import ButtonGroup from '@passkeys/ui/button-group';
import type { IconName } from '@passkeys/ui/icon';

import type { Asset } from '@/types';
import useIsBuySupportedByAsset from '@/ui/hooks/use-is-buy-supported-by-asset';
import useIsSwapSupportedByAsset from '@/ui/hooks/use-is-swap-supported-by-asset';
import useNavigate from '@/ui/hooks/use-navigate';
import { createNavigateStateOptions } from '@/ui/utils/navigation';
import type { BuyPageLocationState } from '../../buy/types.js';
import type { SwapPageLocationState } from '../../swap/types.js';

type ButtonConfig = {
  path: string;
  label: string;
  icon: IconName;
  iconClassName?: string;
  hidden?: boolean;
  navigateOptions?: { state: Record<string, unknown> };
};

interface ActionsProps {
  asset: Asset;
}

function Actions({ asset }: ActionsProps) {
  const { value: swapSupported } = useIsSwapSupportedByAsset(asset.name);
  const { value: buySupported } = useIsBuySupportedByAsset(asset.name);

  const navigate = useNavigate();

  const buttons: ButtonConfig[] = [
    {
      path: `/tokens/${asset.name}/send`,
      label: 'Send',
      icon: 'arrow-up-right',
      iconClassName: 'scale-[1.17]',
    },
    {
      path: '/swap',
      label: 'Swap',
      icon: 'arrow-left-arrow-right-large',
      hidden: !swapSupported,
      navigateOptions: createNavigateStateOptions<SwapPageLocationState>({
        token: asset.name,
        referrer: 'token',
      }),
    },
    {
      path: '/buy',
      label: 'Buy',
      icon: 'currency',
      hidden: !buySupported,
      navigateOptions: createNavigateStateOptions<BuyPageLocationState>({
        token: asset.name,
        referrer: 'token',
      }),
    },
    {
      path: `/tokens/${asset.baseAssetName}/receive`,
      label: 'Receive',
      icon: 'arrow-down-left',
      iconClassName: 'scale-[1.17]',
    },
  ] as const;

  return (
    <ButtonGroup
      buttons={buttons
        .filter(({ hidden }) => !hidden)
        .map(({ path, label, icon, iconClassName, navigateOptions }) => ({
          ariaLabel: label,
          icon,
          iconClassName,
          onClick: () => {
            navigate(path, navigateOptions);
          },
        }))}
    />
  );
}

export default Actions;
